import React, { useContext, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import {
  HelpIcon,
  MenuNotificationsIcon,
  PaymentsIcon,
  ProfileIcon,
  ReferralIcon,
  ServicesIcon,
} from "@monorepo/icons";
import { useGetNotificationsQuery } from "@monorepo/services/api/notifications";
import theme from "@monorepo/styles/theme";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import { Button, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import logoLT from "assets/img/logo.png";
import { reverse } from "named-urls";

import MobileMenu from "components/MobileMenu/MobileMenu";

const Title = styled(Typography)(() => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "block",
  },
}));
const StyledNavLinkBase = styled(NavLink)(() => ({
  display: "flex",
  flexDirection: "column",
  rowGap: "6px",
  color: theme.palette.nobelGray.main,
  textDecoration: "none",
  fontSize: theme.typography.body1.fontSize,
  placeSelf: "center",
  cursor: "pointer",
}));

export const StyledNavLink = styled(StyledNavLinkBase)(() => ({
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",

  "& svg": {
    stroke: "none",
  },
  "&.selected_nav-link": {
    color: theme.palette.primary.main,
    fill: theme.palette.primary.main,
  },
}));

export const Menu: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useContext(ViewportContext);
  const location = useLocation();
  const { data: notifications } = useGetNotificationsQuery();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const showUnreadNotifications = !!notifications?.find(({ seen }) => !seen);
  const firstNotificationId = notifications && notifications.length > 0 ? notifications[0].id : "";

  return (
    <>
      <Box
        sx={{
          marginTop: {
            md: theme.spacing(1),
          },
          overflowX: "hidden",
          "& a": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <NavLink
          onClick={() => {
            ReactGA.event("payments");
          }}
          to={routeConfig.dashboard.payments.self}
        >
          <Box
            draggable={false}
            component="img"
            src={logoLT}
            alt={t("common.perlasgo_logo_alt")}
            sx={{
              width: "90px",
              height: "90px",
              display: {
                xs: "none",
                md: "block",
              },
            }}
          />
        </NavLink>
      </Box>
      <Box
        component="ul"
        sx={{
          maxHeight: { xs: 55, md: "none" },
          borderTop: {
            xs: `1px solid ${theme.palette.geyserGray.main}`,
            md: "none",
          },
          zIndex: 1,
          display: {
            xs: "flex",
            md: "grid",
          },
          justifyContent: "space-around",
          listStyle: "none",
          width: {
            xs: "100vw",
            md: "auto",
          },
          placeSelf: "center",
          "& li": {
            maxHeight: { xs: 55, md: "none" },
            stroke: theme.palette.gullGray.main,
            width: "100%",
            justifyContent: "center",
            alignContent: "center",
            cursor: "pointer",
            display: {
              xs: "inline-flex",
              md: "inline-grid",
            },
            padding: { md: theme.spacing(3, 0) },
          },
        }}
      >
        <li>
          <StyledNavLink
            onClick={() => {
              ReactGA.event("payments");
              setOpenDrawer(false);
            }}
            activeClassName={"selected_nav-link"}
            to={routeConfig.dashboard.payments.self}
          >
            <PaymentsIcon
              fontSize={isMobile ? "medium" : "large"}
              active={!!location.pathname.includes(routeConfig.dashboard.payments.self)}
              monochrome
              activeColor={theme.palette.primary.main}
            />
            <Title>{t("navigation.payments")}</Title>
          </StyledNavLink>
        </li>
        <li>
          <StyledNavLink
            onClick={() => {
              ReactGA.event("other_services");
              setOpenDrawer(false);
            }}
            activeClassName={"selected_nav-link"}
            to={isMobile ? routeConfig.dashboard.services.self : routeConfig.dashboard.services.fishermanTicket}
          >
            <ServicesIcon
              fontSize={isMobile ? "medium" : "large"}
              active={!!location.pathname.includes(routeConfig.dashboard.services.self)}
              monochrome
              activeColor={theme.palette.primary.main}
            />
            <Title>{t("navigation.services")}</Title>
          </StyledNavLink>
        </li>
        {!isMobile ? (
          <li>
            <StyledNavLink
              onClick={() => {
                ReactGA.event("notifications");
              }}
              activeClassName={"selected_nav-link"}
              isActive={() => {
                return !!location.pathname.includes(routeConfig.dashboard.notifications.self);
              }}
              to={
                firstNotificationId
                  ? reverse(routeConfig.dashboard.notifications.notification.notificationId, {
                      notificationId: firstNotificationId,
                    })
                  : routeConfig.dashboard.notifications.self
              }
            >
              <Box
                sx={{
                  position: "relative",
                }}
              >
                <MenuNotificationsIcon
                  fontSize="large"
                  active={!!location.pathname.includes(routeConfig.dashboard.notifications.self)}
                  monochrome
                  activeColor={theme.palette.primary.main}
                />
                {showUnreadNotifications && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "0",
                      right: "7px",
                      width: "8px",
                      height: "8px",
                      backgroundColor: theme.palette.error.dark,
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Box>

              <Title>{t("navigation.notifications")}</Title>
            </StyledNavLink>
          </li>
        ) : (
          <li>
            <StyledNavLinkBase
              onClick={() => {
                ReactGA.event("new_service");
                setOpenDrawer(false);
              }}
              activeClassName={"selected_nav-link"}
              to={routeConfig.dashboard.payments.addService.self}
            >
              <Fab
                color={"primary"}
                sx={{
                  width: "60px",
                  height: "60px",
                  position: "relative",
                  top: "-20px",
                  color: theme.palette.secondary.main,
                }}
              >
                <AddIcon
                  sx={{
                    stroke: "transparent",
                  }}
                  fontSize="large"
                />
              </Fab>
            </StyledNavLinkBase>
          </li>
        )}
        {isMobile && (
          <li>
            <StyledNavLink
              onClick={() => {
                ReactGA.event("notifications");
                setOpenDrawer(false);
              }}
              activeClassName={"selected_nav-link"}
              to={routeConfig.dashboard.notifications.self}
            >
              <Box sx={{ position: "relative" }}>
                <MenuNotificationsIcon
                  active={!!location.pathname.includes(routeConfig.dashboard.notifications.self)}
                  monochrome
                  activeColor={theme.palette.primary.main}
                />
                {showUnreadNotifications && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "0",
                      right: "3px",
                      width: "8px",
                      height: "8px",
                      backgroundColor: theme.palette.error.dark,
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Box>
              <Title>{t("navigation.notifications")}</Title>
            </StyledNavLink>
          </li>
        )}
        {!isMobile && (
          <li>
            <StyledNavLink
              onClick={() => {
                ReactGA.event("profile");
              }}
              isActive={() => {
                return !!location.pathname.includes(routeConfig.dashboard.profile.self);
              }}
              activeClassName={"selected_nav-link"}
              to={routeConfig.dashboard.profile.personal}
            >
              <ProfileIcon
                fontSize="large"
                active={!!location.pathname.includes(routeConfig.dashboard.profile.self)}
                monochrome
                activeColor={theme.palette.primary.main}
              />
              <Title>{t("navigation.profile")}</Title>
            </StyledNavLink>
          </li>
        )}
        {!isMobile && (
          <li>
            <StyledNavLink
              onClick={() => {
                ReactGA.event("referral");
              }}
              isActive={() => {
                return !!location.pathname.includes(routeConfig.dashboard.referral.self);
              }}
              activeClassName={"selected_nav-link"}
              to={routeConfig.dashboard.referral.referral}
            >
              <ReferralIcon
                fontSize="large"
                active={!!location.pathname.includes(routeConfig.dashboard.referral.self)}
                monochrome
                activeColor={theme.palette.primary.main}
              />
              <Title>{t("navigation.referral")}</Title>
            </StyledNavLink>
          </li>
        )}
        {isMobile && (
          <li>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: 0,
              }}
            >
              <Button
                onClick={() => setOpenDrawer((prevState) => !prevState)}
                sx={{ minWidth: "unset", maxHeight: 55, padding: theme.spacing(0), height: "100%", width: "100%" }}
                aria-label={t("aria.open_menu")}
              >
                <MenuIcon sx={{ stroke: "transparent", fill: theme.palette.gullGray.main }} />
              </Button>
            </Box>
          </li>
        )}
      </Box>
      {isMobile && (
        <MobileMenu
          open={openDrawer}
          onToggle={() => {
            setOpenDrawer((prevState) => !prevState);
          }}
        />
      )}
      {!isMobile && (
        <Box sx={{ placeSelf: "center", padding: theme.spacing(3, 0) }}>
          <StyledNavLink activeClassName={"selected_nav-link"} to={routeConfig.dashboard.help.self}>
            <HelpIcon
              sx={{ strokeWidth: "1px" }}
              fontSize="large"
              active={!!location.pathname.includes(routeConfig.dashboard.help.self)}
              monochrome
              activeColor={theme.palette.primary.main}
            />
            <Title>{t("navigation.help")}</Title>
          </StyledNavLink>
        </Box>
      )}
    </>
  );
};
